import React, {createContext, useCallback, useContext, useEffect, useMemo, useReducer, useRef} from 'react';
import {ClientMemoryApi} from '../../../../api/clientMemoryApi';
import {REVIEW_STATUS, VISIBILITY, isLowPriority} from '../../memory/factConstants';

/**
 * Los hechos del cliente, UNA sola vez para toda la página.
 *
 * En el Recorrido one-page los mismos hechos se ven en tres lugares a la vez: el mazo de Revisa,
 * su modo "Lista completa" y el archivo del acto Memoria — y los tres están montados en el mismo
 * DOM. Con estado por componente, aprobar una carta dejaba al archivo mostrando el hecho todavía
 * en la cola hasta que alguien recargara. No es un problema de refresco: son tres respuestas
 * distintas a la misma pregunta, que es exactamente lo que este store impide.
 *
 * Todas las mutaciones son optimistas con rollback: la carta vuela cuando el usuario suelta, no
 * cuando contesta el servidor. Si el PATCH falla, el hecho vuelve a su lugar y se avisa.
 */

// Tope de la pila de deshacer. El mockup usa el mismo número: son suficientes pasos como para
// revertir una sesión entera de curado sin que la pila crezca sin control.
const MAX_UNDO = 60;

/**
 * Cuántas veces se reintenta UNA entrada de deshacer antes de descartarla.
 *
 * Un deshacer puede fallar por un blip: devolverlo a su lugar y dejar que el próximo Z lo reintente
 * es lo correcto. Pero si vuelve a fallar, el problema no es el momento —es la entrada—, y
 * reinsertarla otra vez convierte un fallo puntual en un Deshacer MUERTO: todo Z posterior reintenta
 * la misma entrada, falla igual y nunca llega a las de abajo. Hasta un F5.
 */
const MAX_UNDO_ATTEMPTS = 2;

/**
 * Una fusión que aprobó el pendiente pero NO llegó a superseder al viejo.
 *
 * No es un fallo limpio —el hecho quedó aprobado de verdad, y su paso de deshacer tiene que
 * quedarse— ni un éxito —los dos hechos siguen vivos en la memoria—. El mazo necesita distinguirlo
 * porque sus contadores espejan la pila de deshacer del store entrada por entrada (ver `runStep` en
 * `BoosterReviewDeck`).
 */
export const MERGE_PARTIAL = 'MERGE_PARTIAL';

/**
 * El deshacer falló las veces suficientes y su entrada se descartó: ese cambio ya no tiene vuelta
 * atrás, pero los de abajo sí. Quien lleve un espejo de la pila (el mazo) tiene que soltar su paso
 * en vez de devolverlo.
 */
export const UNDO_DISCARDED = 'UNDO_DISCARDED';

// Espeja `LIST_CAP` de `agent_tools/views/client_fact_views.py`: el listado devuelve como mucho 500
// hechos. Al tocar el tope la lista viene RECORTADA, así que cualquier número derivado de ella
// (empezando por el de la biblioteca) se afirma con un "+" en vez de darse por exacto.
export const FACTS_LIST_CAP = 500;

/**
 * El listado general NO alcanza para armar la cola.
 *
 * `GET agent/client-fact/` sin filtro responde `recent()[:500]` — los 500 más NUEVOS, no "los
 * pendientes". Con 500 aprobados recientes y 40 pendientes viejos, los 40 quedan afuera del corte:
 * el mazo dice "Todo revisado" y la cola real del backend sigue teniendo 40. No es teórico — el tope
 * de 100 hechos por documento hace que cinco documentos grandes llenen el listado.
 *
 * Y `factsTruncated` no lo tapa: avisa que la BIBLIOTECA viene recortada, que es verdad, pero sobre
 * otra cosa. Nadie mira una biblioteca truncada y deduce "puede que te falten pendientes".
 *
 * Por eso son dos listados y no uno, y por eso NO se sube `LIST_CAP`: la cola se pide filtrada
 * (`review_status=PENDING_REVIEW`, que el backend valida contra `REVIEW_STATUS_CHOICES`) y el
 * general queda para la biblioteca. `facts` es la UNIÓN de los dos, así que los selectores, las
 * mutaciones optimistas y el rollback siguen trabajando sobre una sola lista: un hecho aprobado
 * desde la cola no cambia de array, cambia de `review_status` — y aparece en la biblioteca solo.
 */
const factsFromResponse = (data) => {
    // El listado devuelve el array DIRECTO (`{facts: [...]}`), no el envoltorio `{status:'OK', data}`
    // de las mutaciones. Exigir el envoltorio hacía que la carga fallara siempre y el mazo no se
    // llenara nunca — y en un test con fixture propio eso no se ve. Se aceptan las dos formas
    // porque las mutaciones sí devuelven la otra.
    const facts = data && (Array.isArray(data.facts) ? data.facts : data.data);
    return Array.isArray(facts) ? facts : null;
};

/**
 * Une los dos listados sin duplicar ni perder a nadie.
 *
 * El orden se conserva por lista (las dos vienen `recent()`): primero los pendientes —que es el
 * orden en que el mazo reparte las cartas— y después lo que el general agrega. Un hecho que está en
 * los dos se queda con la versión del listado de PENDIENTES: es el request cuyo trabajo es decir qué
 * está en la cola.
 *
 * Si el listado de pendientes falló, se degrada al general: la cola vuelve a poder venir recortada
 * (o sea, exactamente lo de antes de este fix), pero la biblioteca y el archivo siguen andando. Que
 * falle el general sí es una carga fallida — es de donde sale la memoria.
 */
const mergeFactLists = (pending, general) => {
    if (!pending) return general;
    const inQueue = new Set(pending.map(f => f.id));
    return [...pending, ...general.filter(f => !inQueue.has(f.id))];
};

/**
 * Suma el TERCER listado: los rechazados que el triage archivó solo (`auto_archived`). Van al final
 * y sólo los que no estaban ya (un archivado reciente puede venir también en el general). Los
 * rechazados por una persona se descartan acá mismo: siguen invisibles, como siempre. Si el listado
 * falló, no hay archivo — la cola y la biblioteca no dependen de él.
 */
const appendArchived = (facts, archived) => {
    if (!archived) return facts;
    const known = new Set(facts.map(f => f.id));
    return [...facts, ...archived.filter(f => f.auto_archived === true && !known.has(f.id))];
};

/**
 * Id propio de cada entrada de la pila de deshacer.
 *
 * Sin él, el rollback de una operación sacaba "la última" entrada y no la SUYA. Dos decisiones
 * solapadas alcanzan para desincronizar la pila —y solaparlas no cuesta nada: en modo "Lista
 * completa" no hay lock (`isBusy` sólo bloquea el mismo hecho), la sección de ruido llama directo, y
 * el mazo libera `exitingRef` antes de que termine el PATCH—. Si la primera fallaba, se llevaba
 * puesto el paso de la segunda: el hecho realmente aprobado quedaba imposible de deshacer, y el
 * Deshacer mandaba un `reopen` sobre un hecho que ya estaba en la cola… reportando éxito.
 */
let undoSeq = 0;
const nextUndoId = () => {
    undoSeq += 1;
    return `undo-${undoSeq}`;
};

const isOkResponse = (data) => !!(data && data.status === 'OK');

/**
 * ¿El deshacer entró de verdad? Un `{status:'ERROR'}` resuelve la promesa igual que un OK: darlo por
 * bueno consumía el paso Y le avisaba al usuario que revirtió algo que sigue exactamente como estaba.
 */
const undoResponseOk = (result) => (Array.isArray(result)
    ? result.length > 0 && result.every(isOkResponse)
    : isOkResponse(result));

/**
 * Lo que el motor de duplicados de la biblioteca ya decidió por PAR (2026-09-12), tal como lo sirve
 * `GET client-fact/duplicates/`: las propuestas abiertas que necesitan una persona, las fusiones
 * automáticas que todavía se pueden deshacer y tres números. El store NO calcula nada de esto: la
 * comparación corre en el backend, asíncrona por cliente; acá sólo se lee y se resuelve.
 *
 * `failed` es lo que la sección muestra en vez de parecer vacía cuando el GET no llegó; `enabled`
 * es el flag del backend (apagado, la lista viene vacía y honesta).
 */
export const EMPTY_DUPLICATES = Object.freeze({
    enabled: false,
    proposals: [],
    autoMerged: [],
    stats: {open_proposals: 0, pending_scan: 0, auto_merged_total: 0},
    failed: false,
    // El GET tarda (lee la tabla de veredictos de todo el cliente). Mientras no contesta, «no hay
    // duplicados» y «todavía no sé» son lo mismo en pantalla, que es justo lo que la sección existe
    // para evitar. Arranca en `true` y sólo la respuesta —o su fallo— lo baja (QA-VIS-dup-04).
    loading: true,
});

/** El payload del GET, con tolerancia de forma: nada de esto puede tirar abajo la carga de la cola. */
export const duplicatesFromResponse = (data) => ({
    enabled: !!(data && data.enabled),
    proposals: Array.isArray(data && data.proposals) ? data.proposals : [],
    autoMerged: Array.isArray(data && data.auto_merged) ? data.auto_merged : [],
    stats: {...EMPTY_DUPLICATES.stats, ...((data && data.stats) || {})},
    failed: false,
    loading: false,
});

// Los tres grupos de la sección, en el orden en que se leen: primero lo que se cierra con un click,
// después lo que exige decidir. Espeja `ClientFactPairVerdict.KIND_*` del backend.
export const DUPLICATE_KINDS = ['SAME', 'UPDATED', 'CONFLICT'];

/** Las propuestas agrupadas por clase, sin grupos vacíos. Puro: se testea sin montar nada. */
export const duplicatesByKind = (proposals) => DUPLICATE_KINDS
    .map(kind => ({kind, items: (proposals || []).filter(p => p && p.kind === kind)}))
    .filter(group => group.items.length > 0);

/** Cuántos pares esperan una decisión. NO entra al número grande de la cola: es otra pregunta. */
export const duplicatesCount = (duplicates) => ((duplicates && duplicates.proposals) || []).length;

/** Los ids de los dos hechos de una propuesta o de una fusión automática (para marcarlos ocupados). */
export const pairIdsOf = (entry) => [
    entry && entry.older, entry && entry.newer, entry && entry.survivor, entry && entry.merged,
].filter(fact => fact && fact.id != null).map(fact => fact.id);

const INITIAL = {
    facts: [],
    // El tercer listado (lo que archivó el triage) no llegó: la sección lo dice en vez de parecer vacía.
    archivedFailed: false,
    // El listado GENERAL vino recortado en `FACTS_LIST_CAP`, o sea que la biblioteca que se muestra
    // no es toda la que hay. No se deduce de `facts.length`: `facts` es la unión de dos listados y
    // puede pasarse del tope sin que ninguno de los dos esté recortado.
    libraryTruncated: false,
    documentsById: {},
    duplicates: EMPTY_DUPLICATES,
    loading: true,
    loadFailed: false,
    // Ids con una operación en vuelo: la UI los deshabilita para que un doble click no mande dos
    // PATCH del mismo hecho.
    busyIds: [],
    undoStack: [],
};

// --- Selectores. Puros y exportados: se testean sin montar nada, y son la ÚNICA definición de
// qué es "la cola", "el ruido" y "la biblioteca". ---

/** Lo que espera revisión de verdad: pendientes que el triage no degradó a ruido. */
export const reviewQueue = (facts) => facts.filter(
    f => f.review_status === REVIEW_STATUS.PENDING && !isLowPriority(f));

/** Pendientes que el triage marcó como ruido probable: se descartan por familia, no de a uno. */
export const noiseQueue = (facts) => facts.filter(
    f => f.review_status === REVIEW_STATUS.PENDING && isLowPriority(f));

/** La memoria propiamente dicha: lo aprobado y vigente. */
export const library = (facts) => facts.filter(
    f => f.review_status === REVIEW_STATUS.APPROVED && f.is_active);

/**
 * El número grande: lo que espera una decisión de a uno. El ruido probable NO cuenta acá: desde que
 * el triage archiva solo las familias seguras y el resto se descarta por grupo, contarlo hacía que
 * el umbral prometiera "40 esperan tu ok" con tres cartas reales en el mazo.
 */
export const pendingCount = (facts) => reviewQueue(facts).length;

/** Cuántos pendientes son ruido probable: la sección de ruido muestra ESTE número, no el grande. */
export const noiseCount = (facts) => noiseQueue(facts).length;

/** Tope de «Archivado solo»: alcanza para buscar algo sin que el archivo compita con la biblioteca. */
export const ARCHIVED_CAP = 200;

/**
 * Cuántos archivados hay de verdad, antes del tope. Lo que la lista NO muestra tiene que poder
 * decirse: un archivo que se corta en 200 sin avisar es exactamente «descartar en silencio», sólo
 * que del lado de la pantalla — y eso es justo lo que este frente vino a sacar. Con la compuerta
 * abierta al material interno de la agencia un solo documento puede llenar la ventana, así que deja
 * de ser un caso teórico.
 *
 * ⚠️ Este número es un PISO, no el total (review C-cola-08): cuenta sobre `state.facts`, que sale de
 * un GET capado en `FACTS_LIST_CAP`. Con más de 500 archivados podrían ser miles, y un aviso que
 * dice «hay 300 más» cuando hay 3.000 sería el mismo descarte silencioso una capa más arriba. Por
 * eso el copy dice «al menos» y no un número exacto.
 */
export const archivedTotal = (facts) => facts
    .filter(f => f.review_status === REVIEW_STATUS.REJECTED && f.auto_archived === true).length;

/**
 * Lo que el triage archivó solo: rechazado sin persona (`auto_archived`, que el backend deriva), más
 * nuevo primero. Un hecho que rechazó una persona NO está acá aunque esté rechazado: lo archivó ella
 * y su vuelta atrás es el Deshacer.
 */
export const archivedQueue = (facts) => facts
    .filter(f => f.review_status === REVIEW_STATUS.REJECTED && f.auto_archived === true)
    .sort((a, b) => (Date.parse(b.created_on) || 0) - (Date.parse(a.created_on) || 0))
    .slice(0, ARCHIVED_CAP);

/**
 * Si este hecho entró a la memoria sin que una persona lo tocara.
 *
 * Es la marca SOLA. Hasta el 2026-09-08 esto venía envuelto en un selector con ventana de 7 días
 * y tope de 50 filas (`autoEntered`), porque alimentaba una bandeja de auditoría al pie de Revisa:
 * «qué entró solo esta semana». Esa bandeja se integró al archivo de la Memoria y con eso cambió
 * la pregunta — ahí la marca es **procedencia**, y la procedencia no vence: un hecho que perdiera
 * su etiqueta al octavo día estaría diciendo que alguien lo aprobó.
 *
 * En Dorfman la diferencia no es teórica: 60 de sus 62 hechos entraron solos y la bandeja mostraba
 * CERO, porque ninguno de ellos cabía en los 7 días.
 */
export const isAutoEntered = (fact) => !!fact && fact.auto_approved === true;

function reducer(state, action) {
    switch (action.type) {
        case 'LOADED':
            return {
                ...state,
                facts: action.facts,
                libraryTruncated: !!action.libraryTruncated,
                archivedFailed: !!action.archivedFailed,
                loading: false,
                loadFailed: false,
            };
        case 'LOAD_FAILED':
            return {...state, loading: false, loadFailed: true};
        case 'DOCUMENTS':
            return {...state, documentsById: action.documentsById};
        case 'DUPLICATES':
            return {...state, duplicates: action.duplicates};
        case 'PATCH_LOCAL':
            return {
                ...state,
                facts: state.facts.map(f => (f.id === action.factId ? {...f, ...action.changes} : f)),
            };
        case 'REPLACE_FACTS': {
            // La respuesta del bulk trae los hechos ya mutados: se reemplazan por id en vez de
            // refetchear la lista entera.
            const byId = {};
            action.facts.forEach(f => { byId[f.id] = f; });
            return {...state, facts: state.facts.map(f => byId[f.id] || f)};
        }
        case 'RESTORE_FACT':
            // Rollback: el hecho vuelve exactamente a como estaba antes del intento.
            return {
                ...state,
                facts: state.facts.map(f => (f.id === action.fact.id ? action.fact : f)),
            };
        case 'REMOVE_FACT':
            return {...state, facts: state.facts.filter(f => f.id !== action.factId)};
        case 'ADD_FACT':
            return {...state, facts: [action.fact, ...state.facts]};
        case 'BUSY': {
            const ids = new Set(state.busyIds);
            action.ids.forEach(id => (action.busy ? ids.add(id) : ids.delete(id)));
            return {...state, busyIds: [...ids]};
        }
        case 'PUSH_UNDO':
            return {...state, undoStack: [...state.undoStack, action.entry].slice(-MAX_UNDO)};
        case 'DROP_UNDO':
            // Por ID, nunca por posición: para cuando contesta el servidor, la entrada que empujó
            // esta operación puede tener otras encima.
            return {
                ...state,
                undoStack: state.undoStack.filter(entry => entry.undoId !== action.undoId),
            };
        case 'AMEND_UNDO':
            // Corrige las ops de una entrada YA empujada, EN SU LUGAR (no se saca y se re-empuja:
            // eso cambiaría el orden de la pila). Una operación de dos PATCH no puede saber, cuando
            // empuja su paso, cuáles de los dos van a entrar; si el segundo falla, la reversión
            // correcta es más chica que la que se anticipó — y mandar la grande es lo que mata la
            // cadena, porque el backend rechaza esa op y el paso vuelve a la pila para siempre.
            //
            // ⚠️ Desde el review del 26/09 (C-knob2-01) también corrige una entrada de TANDA, por la
            // misma razón un nivel más arriba: con `skip_missing` el backend puede mover MENOS
            // hechos de los que se le mandaron, y la reversión tiene que apuntar sólo a los que se
            // movieron. Si no, el Deshacer manda `reopen` sobre un hecho que otra persona rechazó y
            // **revierte una decisión humana con la firma de este usuario**.
            return {
                ...state,
                undoStack: state.undoStack.map((entry) => {
                    if (entry.undoId !== action.undoId) return entry;
                    const amended = {...entry};
                    if (action.ops) amended.ops = action.ops;
                    if (action.bulk) amended.bulk = action.bulk;
                    if (action.count !== undefined) amended.count = action.count;
                    return amended;
                }),
            };
        case 'REINSERT_UNDO': {
            // El deshacer que no entró vuelve a SU lugar. Re-empujarlo al final dejaría la pila en
            // un orden que el usuario nunca ejecutó.
            const stack = state.undoStack.filter(entry => entry.undoId !== action.entry.undoId);
            stack.splice(Math.max(0, Math.min(action.index, stack.length)), 0, action.entry);
            return {...state, undoStack: stack.slice(-MAX_UNDO)};
        }
        default:
            return state;
    }
}

const ClientFactsContext = createContext(null);

/**
 * `hasFactsLayer`: si esta agencia tiene la capa de Hechos (nivel TOTAL de la Memoria del Cliente).
 * En MEDIO no hay hechos, y en OFF el acto Memoria sobrevive sólo por la Bitácora: en los dos casos
 * los tres endpoints de hechos no tienen nada que devolver (el backend responde 404 uniforme), así
 * que pedirlos es gastar tres requests para llenar de errores la consola. Con la capa apagada el
 * store queda vacío y CARGADO — no "cargando": un spinner eterno en el archivo sería peor que el
 * vacío honesto.
 */
export function ClientFactsProvider({
    clientId, hasFactsLayer = true, onPendingCountChange, onApprovedCountChange, children,
}) {
    const [state, dispatch] = useReducer(reducer, INITIAL);
    const factsRef = useRef(state.facts);
    factsRef.current = state.facts;

    // Nº de la carga en curso. Una respuesta vieja no puede pisar a una nueva (ni dispatchear
    // después del desmontaje): es la misma guarda que los tres efectos del contenedor resuelven con
    // su bandera `alive`, y acá faltaba. Desmontar invalida todo lo que esté en vuelo.
    const fetchSeqRef = useRef(0);
    useEffect(() => () => { fetchSeqRef.current += 1; }, []);

    const factById = useCallback(
        (id) => factsRef.current.find(f => f.id === id) || null,
        [],
    );

    // Devuelve una promesa que resuelve cuando contestaron TODOS los listados. La necesita
    // `resolveDuplicate`: sin esperar el refetch, la fila resuelta se queda en pantalla con sus
    // botones habilitados hasta que vuelve (QA-VIS-dup-05).
    const fetchAll = useCallback(() => {
        if (!hasFactsLayer) return Promise.resolve();
        fetchSeqRef.current += 1;
        const seq = fetchSeqRef.current;
        const fresh = () => fetchSeqRef.current === seq;

        // La cola y la biblioteca son DOS listados (ver `mergeFactLists`): el general trae "los 500
        // más nuevos", que no es lo mismo que "los pendientes".
        //
        // Se dispatchea UNA sola vez, cuando contestaron los dos. Publicar el general apenas llega
        // dejaría al mazo diciendo "Todo revisado" mientras el otro request sigue en vuelo — el
        // mismo cartel equivocado de siempre, sólo que por menos tiempo y con el usuario ya
        // haciendo click. El `fresh()` cubre a los dos porque el único dispatch es éste.
        const listFacts = (options) => ClientMemoryApi.listFacts(clientId, options)
            .then(factsFromResponse)
            .catch(() => null);

        const factsDone = Promise.all([
            listFacts({reviewStatus: REVIEW_STATUS.PENDING}),
            listFacts(),
            // El archivo del triage (`auto_archived=1`): lo filtra el server, así no compite con
            // los rechazos humanos por el tope del listado ni viaja de más (C-memoria-27).
            listFacts({autoArchived: true}),
        ]).then(([pending, general, archived]) => {
            if (!fresh()) return;
            if (!general) {
                dispatch({type: 'LOAD_FAILED'});
                return;
            }
            dispatch({
                type: 'LOADED',
                facts: appendArchived(mergeFactLists(pending, general), archived),
                libraryTruncated: general.length >= FACTS_LIST_CAP,
                archivedFailed: archived === null,
            });
        });

        // La procedencia de cada hecho (de qué documento salió) se muestra en la carta y en el
        // archivo. Fail-soft: sin documentos las cartas pierden la cita, no la acción.
        ClientMemoryApi.listDocuments(clientId).then((data) => {
            if (!fresh()) return;
            const docs = (data && (data.documents || data.data)) || [];
            const byId = {};
            (Array.isArray(docs) ? docs : []).forEach(d => { byId[d.id] = d; });
            dispatch({type: 'DOCUMENTS', documentsById: byId});
        }).catch(() => {});

        // Los duplicados que el motor ya decidió por par. Fail-soft, pero DICHO: sin esto la
        // sección de Revisa quedaba vacía y muda cuando el GET no llegaba, indistinguible de «no
        // hay duplicados».
        const duplicatesDone = ClientMemoryApi.listDuplicates(clientId).then((data) => {
            if (!fresh()) return;
            dispatch({type: 'DUPLICATES', duplicates: duplicatesFromResponse(data)});
        }).catch(() => {
            if (fresh()) {
                dispatch({type: 'DUPLICATES',
                          duplicates: {...EMPTY_DUPLICATES, failed: true, loading: false}});
            }
        });

        return Promise.all([factsDone, duplicatesDone]);
    }, [clientId, hasFactsLayer]);

    useEffect(() => {
        if (!hasFactsLayer) {
            dispatch({type: 'LOADED', facts: []});
            return;
        }
        fetchAll();
    }, [fetchAll, hasFactsLayer]);

    // El badge del sidebar y el umbral de Revisa salen del poller global, no de acá: se le avisa
    // el número nuevo para que se corrija en el acto (optimista) en vez de esperar al próximo
    // poll. El override del contexto se limpia solo en cada poll exitoso.
    const pending = pendingCount(state.facts);
    // El segundo argumento es el RUIDO, y viaja junto porque el lede del acto necesita las dos
    // cosas para decir la verdad: «por ahora no hay nada esperando tu ok» con la cola principal en
    // cero es falso si hay catorce hechos de ruido listados abajo (QA del 10/09, QA-VIS-booster-03).
    // Va como argumento opcional y no como callback nuevo: quien no lo lea se comporta igual que antes.
    const noise = noiseCount(state.facts);
    useEffect(() => {
        if (!state.loading && onPendingCountChange) onPendingCountChange(pending, noise);
    }, [pending, noise, state.loading, onPendingCountChange]);

    // La biblioteca real. El umbral «lo que apruebas entra a la memoria» sale de acá y no de un
    // contador incremental del contenedor: ese contador se sumaba de forma optimista sin revertirse
    // ante un fallo, no lo tocaban ni la fusión ni el bulk, y deshacer no restaba. Esta lista se
    // mueve con TODAS las acciones (incluido el undo, que refetchea) y no puede mentir.
    const libraryFacts = useMemo(() => library(state.facts), [state.facts]);
    // `null` = "no lo sé", que no es lo mismo que cero: si el listado falló, afirmar 0 hechos
    // aprobados sería decir que la memoria está vacía.
    const approvedCount = state.loading || state.loadFailed ? null : libraryFacts.length;
    // Sale del listado GENERAL, no de `facts.length`: desde que la cola se pide aparte, la unión de
    // los dos puede pasar los 500 sin que la biblioteca esté recortada (y al revés, con 500
    // aprobados y 40 pendientes la unión da 540 — el "+" tiene que salir igual, porque el recorte
    // que importa es el del listado del que sale este número).
    const factsTruncated = state.libraryTruncated;
    useEffect(() => {
        if (onApprovedCountChange) onApprovedCountChange(approvedCount, factsTruncated);
    }, [approvedCount, factsTruncated, onApprovedCountChange]);

    /**
     * Una mutación optimista: aplica el cambio, manda el PATCH y, si falla, restaura el hecho tal
     * cual estaba. `undo` es la lista de operaciones que la revierten (ver `undoLast`).
     */
    const mutate = useCallback((factId, changes, payload, undo) => {
        const before = factById(factId);
        if (!before) return Promise.resolve(false);
        // La entrada se identifica ANTES de mandar nada: el rollback tiene que poder sacar
        // exactamente ésta, esté donde esté en la pila cuando conteste el servidor. El llamador
        // puede traer el id ya asignado (`mergeAndApprove`) porque necesita poder CORREGIR su
        // entrada después, no sólo sacarla.
        const entry = undo ? {...undo, undoId: undo.undoId || nextUndoId()} : null;
        dispatch({type: 'BUSY', ids: [factId], busy: true});
        dispatch({type: 'PATCH_LOCAL', factId, changes});
        if (entry) dispatch({type: 'PUSH_UNDO', entry});
        return ClientMemoryApi.patchFact(factId, {agency_client_id: clientId, ...payload})
            .then((data) => {
                const ok = !!(data && data.status === 'OK');
                if (!ok) {
                    dispatch({type: 'RESTORE_FACT', fact: before});
                    if (entry) dispatch({type: 'DROP_UNDO', undoId: entry.undoId});
                } else if (data.data) {
                    // El backend es la verdad sobre el estado final (puede tocar campos que el
                    // optimismo no anticipó, como `reviewed_by` o el embedding).
                    dispatch({type: 'PATCH_LOCAL', factId, changes: data.data});
                }
                return ok;
            })
            .catch(() => {
                dispatch({type: 'RESTORE_FACT', fact: before});
                if (entry) dispatch({type: 'DROP_UNDO', undoId: entry.undoId});
                return false;
            })
            .finally(() => dispatch({type: 'BUSY', ids: [factId], busy: false}));
    }, [clientId, factById]);

    const approve = useCallback((factId) => mutate(
        factId,
        {review_status: REVIEW_STATUS.APPROVED, is_active: true},
        {action: 'approve'},
        // `reopen` y no `approve`/`reject`: el `approve()` del backend NO restaura `is_active`,
        // así que revertir con las acciones normales dejaba el hecho aprobado pero invisible.
        {kind: 'approve', ops: [{factId, payload: {action: 'reopen'}}]},
    ), [mutate]);

    const reject = useCallback((factId) => mutate(
        factId,
        {review_status: REVIEW_STATUS.REJECTED, is_active: false},
        {action: 'reject'},
        {kind: 'reject', ops: [{factId, payload: {action: 'reopen'}}]},
    ), [mutate]);

    /**
     * «Quitar» desde el ARCHIVO de la Memoria (la fila marcada «Entró solo»).
     *
     * Rechaza con el mismo PATCH que el mazo (`reject`), pero **NO empuja entrada de deshacer**, y
     * eso no es un olvido: la pila del store la consume el mazo, que sostiene *un paso de sesión
     * por cada entrada, del mismo tamaño y en el mismo orden* (`runStep` en `BoosterReviewDeck`).
     * Los dos actos están montados a la vez en la misma página, así que una entrada empujada desde
     * Memoria le dejaría al Z del mazo una entrada sin paso: el próximo deshacer revertiría ESTE
     * hecho y el rail restaría un aprobado que sigue aprobado.
     *
     * La vuelta atrás del archivo es `restoreToLibrary`, que hace lo mismo que haría el deshacer y
     * no depende de la pila.
     */
    const removeFromArchive = useCallback((factId) => mutate(
        factId,
        {review_status: REVIEW_STATUS.REJECTED, is_active: false},
        {action: 'reject'},
    ), [mutate]);

    /**
     * Reponer en la memoria lo que se quitó desde el archivo: `reopen` y después `approve`, EN
     * ORDEN y sobre el mismo hecho (dos PATCH simultáneos sobre una fila se pisan).
     *
     * Es la MISMA cadena que el deshacer de «Quitar» en el mazo, por el mismo motivo: `approve`
     * solo no alcanza porque el backend no restaura `_is_active` y dejaría el hecho aprobado pero
     * invisible, y `restore` no acepta un rechazado (QA 2026-09-06, caso 05). Sin optimismo: se
     * refetchea, que es lo que hace `undoLast` cuando su cadena entra.
     */
    const restoreToLibrary = useCallback((factId) => {
        const patch = action => ClientMemoryApi.patchFact(factId, {agency_client_id: clientId, action});
        return patch('reopen').then((first) => {
            if (!isOkResponse(first)) return false;
            /**
             * El `reopen` YA entró. Pase lo que pase con el `approve`, el backend cambió y el
             * estado local dejó de valer: se refetchea SIEMPRE, no sólo cuando sale bien.
             *
             * Sin esto, una cadena a medias deja el hecho PENDIENTE allá —sumado a la cola del mazo
             * y al badge de toda la app— y RECHAZADO acá, con el usuario leyendo un error que dice
             * que no se pudo. Esa divergencia sobrevive hasta el próximo F5 (review 2026-09-09,
             * R-05: el test viejo cubría el fallo del PRIMER PATCH, no el del segundo).
             */
            return patch('approve')
                .then(isOkResponse)
                .catch(() => false)
                .then((ok) => { fetchAll(); return ok; });
        }).catch(() => false);
    }, [clientId, fetchAll]);

    /**
     * «Revisar» desde «Entró solo»: el hecho vuelve a la cola para que una persona lo decida.
     *
     * Sin entrada de deshacer, a propósito. Volver a la cola ES el deshacer de "entró solo", y el
     * camino de vuelta ya existe (aprobarlo desde el mazo). Una entrada acá además desalinearía la
     * pila de deshacer con los pasos de sesión del mazo, que sólo cuentan aprobados y rechazados
     * (ver `runStep` en `BoosterReviewDeck`). `auto_approved` se apaga en el optimismo: el backend
     * estampa `reviewed_by` al reabrir, así que el hecho deja de ser "entró solo" también allá.
     */
    const reopen = useCallback((factId) => mutate(
        factId,
        {review_status: REVIEW_STATUS.PENDING, is_active: true, auto_approved: false},
        {action: 'reopen'},
        null,
    ), [mutate]);

    /**
     * «Restaurar» desde «Archivado solo»: el hecho que el triage archivó vuelve a la cola PRINCIPAL
     * (`review_priority` en null), no a «Ruido probable» — si una persona lo rescató del archivo, vale
     * una decisión de verdad.
     *
     * CON deshacer desde el 2026-09-06, por SIMETRÍA con «Quitar», su acción vecina: dos botones que
     * están uno al lado del otro y se comportan distinto es lo que hace desconfiar de los dos. Lo
     * deshace `rearchive`, una acción de backend nueva — con las que ya había no alcanzaba, porque
     * `reject` deja el hecho fuera de «Archivado solo» para siempre. Una sola op, así que no necesita
     * `sequential`. El deshacer NO borra el rastro: los dos movimientos quedan en la Bitácora,
     * atribuidos a quien los hizo.
     */
    const unarchive = useCallback((factId) => mutate(
        factId,
        {review_status: REVIEW_STATUS.PENDING, is_active: true, review_priority: null,
            auto_archived: false},
        {action: 'unarchive'},
        {kind: 'unarchive', ops: [{factId, payload: {action: 'rearchive'}}]},
    ), [mutate]);

    // Editar y aprobar en un solo request: el endpoint acepta `action` y `statement` juntos, así
    // que no hay ventana en la que el hecho quede editado pero sin aprobar.
    const editAndApprove = useCallback((factId, statement) => {
        const before = factById(factId);
        const previous = before ? before.statement : null;
        return mutate(
            factId,
            {statement, review_status: REVIEW_STATUS.APPROVED, is_active: true},
            {action: 'approve', statement},
            {kind: 'edit', ops: [{factId, payload: {action: 'reopen', statement: previous}}]},
        );
    }, [factById, mutate]);

    /**
     * Fusionar un duplicado: el pendiente (redacción fresca, y la que el usuario pudo editar en la
     * carta) sobrevive, y el aprobado viejo queda superseded. Son dos PATCH porque son dos hechos.
     *
     * Y por eso hay tres desenlaces, no dos: si entra el primero y falla el segundo, el hecho quedó
     * aprobado de verdad (`MERGE_PARTIAL`). Ver el comentario de `rollbackDup`.
     */
    const mergeAndApprove = useCallback((pendingId, approvedDupId, statement) => {
        // El id de la entrada se fija ACÁ y no adentro de `mutate`: esta operación es la única que
        // puede necesitar corregir su propio paso de deshacer una vez empujado.
        const undoId = nextUndoId();
        const undo = {
            kind: 'merge',
            undoId,
            ops: [
                {factId: pendingId, payload: {action: 'reopen'}},
                // El viejo estaba APROBADO: `reopen` lo mandaría a la cola, que es un estado en el
                // que nunca estuvo. `restore` lo devuelve a aprobado y activo.
                {factId: approvedDupId, payload: {action: 'restore'}},
            ],
        };
        const payload = statement ? {action: 'approve', statement} : {action: 'approve'};
        // Sin edición NO se toca el statement: un `{statement: undefined}` en el spread del
        // optimismo pisa el texto con `undefined` y el hecho fusionado aparece VACÍO en el archivo
        // hasta el próximo refetch. Fusionar sin editar es el camino normal del mazo.
        const changes = {review_status: REVIEW_STATUS.APPROVED, is_active: true};
        if (statement) changes.statement = statement;
        return mutate(pendingId, changes, payload, undo).then((ok) => {
            if (!ok) return false;
            // El optimismo del SEGUNDO hecho también necesita rollback. Sin esto, un fallo del PATCH
            // de supersede dejaba al viejo fuera del archivo (`is_active:false`) mientras el backend
            // lo sigue teniendo aprobado y activo — y el MCP sirviéndolo. La pantalla decía una
            // cosa y la memoria del cliente otra, sin ningún error a la vista.
            const beforeDup = factById(approvedDupId);
            const rollbackDup = () => {
                if (beforeDup) dispatch({type: 'RESTORE_FACT', fact: beforeDup});
                // Y el paso de deshacer tiene que describir lo que EFECTIVAMENTE pasó, no lo que se
                // intentó: el viejo nunca quedó superseded, así que su `restore` sobra. Peor que
                // sobrar — el backend lo rechaza con 400 (`can_be_restored()` exige un hecho
                // reemplazado, o aprobado e inactivo), el deshacer nunca entra y la entrada vuelve
                // a la pila: todo Z posterior reintenta ÉSTA y la red muere hasta un F5. Mientras
                // tanto el `reopen` del pendiente sí se aplicaba en cada intento, sin refetch.
                dispatch({
                    type: 'AMEND_UNDO',
                    undoId,
                    ops: [{factId: pendingId, payload: {action: 'reopen'}}],
                });
                // Ni `true` (los dos hechos siguen vivos) ni `false` (el pendiente SÍ se aprobó y
                // su paso de deshacer se queda en la pila): quien cuente pasos necesita saberlo.
                return MERGE_PARTIAL;
            };
            dispatch({type: 'PATCH_LOCAL', factId: approvedDupId, changes: {is_active: false}});
            return ClientMemoryApi.patchFact(approvedDupId, {
                agency_client_id: clientId, supersede_by_id: pendingId,
            }).then(r => (isOkResponse(r) ? true : rollbackDup())).catch(rollbackDup);
        });
    }, [clientId, factById, mutate]);

    /**
     * Compartir (o dejar de compartir) un hecho con el cliente.
     *
     * **No empuja entrada de deshacer, y ésta es la razón** (review 2026-09-09, R-01): la pila la
     * consume el mazo, que sostiene *un paso de sesión por cada entrada, del mismo tamaño y en el
     * mismo orden* (`runStep` en `BoosterReviewDeck`), y sus pasos sólo cuentan aprobados,
     * rechazados y ruido. Este toggle se dispara desde el archivo de la Memoria, que está montado
     * AL MISMO TIEMPO que el mazo: hasta hoy empujaba su entrada sin paso que la espejara, así que
     * aprobar una carta en Revisa → compartir un hecho en Memoria → `Ctrl+Z` en Revisa revertía el
     * COMPARTIR mientras el rail descontaba el APROBADO, que seguía aprobado.
     *
     * Lo que se pierde es deshacer un compartir con el teclado — pero este control **es su propio
     * deshacer**: volver a clickearlo lo devuelve, en un click y sin ambigüedad sobre qué se
     * revierte. Mismo criterio que `removeFromArchive`.
     */
    const toggleVisibility = useCallback((factId) => {
        const before = factById(factId);
        if (!before) return Promise.resolve(false);
        const next = before.visibility === VISIBILITY.SHARED
            ? VISIBILITY.AGENCY_ONLY
            : VISIBILITY.SHARED;
        return mutate(factId, {visibility: next}, {visibility: next});
    }, [factById, mutate]);

    const removeFact = useCallback((factId) => {
        const before = factById(factId);
        if (!before) return Promise.resolve(false);
        dispatch({type: 'REMOVE_FACT', factId});
        return ClientMemoryApi.deleteFact(factId, clientId).then((data) => {
            const ok = !!(data && data.status === 'OK');
            // El borrado es soft del lado del backend, pero acá no se ofrece deshacer: la fila ya
            // no está y reponerla en su posición original sería adivinar. Si falla, vuelve.
            if (!ok) dispatch({type: 'ADD_FACT', fact: before});
            return ok;
        }).catch(() => {
            dispatch({type: 'ADD_FACT', fact: before});
            return false;
        });
    }, [clientId, factById]);

    /**
     * Resolver una tanda entera. UN request, no N: con 50 pendientes, N PATCH en paralelo son 50
     * requests, 50 filas en la Bitácora y 50 encolados de embedding — y deshacerlo, otros 50. El
     * backend lo resuelve todo-o-nada.
     *
     * Vale para los dos lados: «Aprobar todo» del rail y el descarte por familia de la cola de
     * ruido, que puede ser 40 hechos de un saque.
     */
    // `skipMissing` / `onSkipped`: ver `approveAll`. El valor de retorno NO cambia —sigue siendo el
    // booleano que todos los llamadores comparan contra `false`— porque devolver un conteo rompería a
    // todos: `0` es falsy y se leería como «falló».
    const bulkReview = useCallback((action, factIds, {skipMissing, onSkipped} = {}) => {
        if (!factIds.length) return Promise.resolve(false);
        const before = factIds.map(factById).filter(Boolean);
        const changes = action === 'approve'
            ? {review_status: REVIEW_STATUS.APPROVED, is_active: true}
            : {review_status: REVIEW_STATUS.REJECTED, is_active: false};
        const entry = {
            kind: 'bulk', bulk: {action: 'reopen', factIds}, count: factIds.length,
            undoId: nextUndoId(),
        };
        dispatch({type: 'BUSY', ids: factIds, busy: true});
        factIds.forEach(factId => dispatch({type: 'PATCH_LOCAL', factId, changes}));
        dispatch({type: 'PUSH_UNDO', entry});
        return ClientMemoryApi.bulkReviewFacts(clientId, action, factIds, {skipMissing})
            .then((data) => {
            const ok = !!(data && data.status === 'OK');
            if (!ok) {
                before.forEach(fact => dispatch({type: 'RESTORE_FACT', fact}));
                dispatch({type: 'DROP_UNDO', undoId: entry.undoId});
                return false;
            }
            const returned = Array.isArray(data.facts) ? data.facts : [];
            if (returned.length) dispatch({type: 'REPLACE_FACTS', facts: returned});

            // 🔴 El 200 PARCIAL. Con `skip_missing` el backend mueve menos hechos de los que se le
            // mandaron, y hasta el review del 26/09 (C-knob2-01) el optimismo quedaba en pie sobre
            // los salteados: se veían APROBADOS y fuera de la cola aunque otra persona los hubiera
            // rechazado, y el Deshacer los incluía. Tres cosas hay que deshacer acá, no una.
            //
            // ⚠️ El disparador es `skipped`, NO «faltan hechos en la respuesta»: hay llamadores cuyo
            // 200 no trae `facts` (el `reopen` del propio Deshacer), y tratarlos como tanda parcial
            // revertía el optimismo y tiraba la entrada de deshacer de una tanda que entró completa.
            // Un backend que todavía no manda `skipped` cae acá en 0 y se comporta como antes.
            const skipped = Number(data.skipped) || 0;
            const moved = new Set(returned.map(fact => fact.id));
            const untouched = skipped > 0 ? factIds.filter(id => !moved.has(id)) : [];
            if (untouched.length) {
                // (1) El optimismo se revierte SÓLO sobre los que no se movieron.
                before.filter(fact => untouched.includes(fact.id))
                    .forEach(fact => dispatch({type: 'RESTORE_FACT', fact}));
                // (2) La entrada de Deshacer se recorta a lo que de verdad se movió. Si no quedó
                // nada, se va: una entrada vacía le daría al Ctrl+Z un paso que no revierte nada.
                const movedIds = factIds.filter(id => moved.has(id));
                if (movedIds.length) {
                    dispatch({
                        type: 'AMEND_UNDO', undoId: entry.undoId, count: movedIds.length,
                        bulk: {...entry.bulk, factIds: movedIds},
                    });
                } else {
                    dispatch({type: 'DROP_UNDO', undoId: entry.undoId});
                }
                // (3) Y el estado REAL de los salteados no lo sabemos: `before` es lo que había
                // antes de nuestro intento, no lo que otra pestaña escribió mientras tanto. El
                // refetch es lo único que lo reconcilia; la restauración de (1) es lo que evita
                // mostrar una mentira en el medio.
                fetchAll();
            }
            // Cuántos salteó el backend, para que quien pidió tolerancia pueda DECIRLO. Se avisa por
            // callback y no por el valor de retorno para no tocarle el contrato a los otros
            // llamadores. `skipped` puede no venir (backend viejo): ahí es 0 y nadie dice nada.
            if (onSkipped) onSkipped(skipped);
            return true;
        }).catch(() => {
            before.forEach(fact => dispatch({type: 'RESTORE_FACT', fact}));
            dispatch({type: 'DROP_UNDO', undoId: entry.undoId});
            return false;
        }).finally(() => dispatch({type: 'BUSY', ids: factIds, busy: false}));
    }, [clientId, factById, fetchAll]);

    /**
     * `options` (las dos opcionales, y por default el comportamiento es el de siempre):
     * - `skipMissing`: el backend saltea los ids que ya no resuelven o dejaron de ser aprobables en
     *   vez de rechazar la tanda entera. Lo pide **sólo la oferta del knob**, cuyos ids salen de un
     *   GET anterior; «Aprobar todo» no, porque los suyos salen del estado vivo del mazo.
     * - `onSkipped(n)`: cuántos salteó, para poder nombrarlo en pantalla.
     */
    const approveAll = useCallback(
        (factIds, options) => bulkReview('approve', factIds, options), [bulkReview]);
    const rejectAll = useCallback((factIds) => bulkReview('reject', factIds), [bulkReview]);

    /**
     * Deshacer el último cambio. Encadenado: cada Z retrocede un paso más, hasta el fondo de la
     * pila. Es la red que hace que aprobar rápido no dé miedo — sin esto, el mazo obligaría a
     * pensar cada carta como irreversible.
     */
    const undoLast = useCallback(() => {
        const index = state.undoStack.length - 1;
        const entry = state.undoStack[index];
        if (!entry) return Promise.resolve(false);
        dispatch({type: 'DROP_UNDO', undoId: entry.undoId});

        const patch = (op) => ClientMemoryApi.patchFact(
            op.factId, {agency_client_id: clientId, ...op.payload});
        // `sequential`: las ops caen sobre el MISMO hecho y el orden importa, así que no pueden ir
        // en paralelo (dos PATCH simultáneos sobre una fila se pisan). Lo usa el deshacer de
        // «Quitar», que repone el hecho con `reopen` y después `approve` (QA 2026-09-06, caso 05).
        // Si una op falla, la cadena se corta ahí y la entrada vuelve a la pila.
        const request = entry.bulk
            ? ClientMemoryApi.bulkReviewFacts(clientId, entry.bulk.action, entry.bulk.factIds)
            : (entry.sequential
                ? entry.ops.reduce(
                    // El acumulador arranca vacío, y `undoResponseOk([])` es false: la primera op
                    // va siempre, y de la segunda en adelante sólo si las anteriores salieron bien.
                    (chain, op, i) => chain.then((done) => (
                        i === 0 || undoResponseOk(done)
                            ? patch(op).then(r => [...done, r])
                            : done)),
                    Promise.resolve([]))
                : Promise.all(entry.ops.map(patch)));

        // Si el deshacer no entró, el paso NO se consumió. Sin esto `undoDepth` bajaba igual, el
        // hecho seguía aprobado y ese paso quedaba imposible de deshacer para siempre.
        //
        // Pero devolverlo SIEMPRE tiene el defecto simétrico: una entrada que falla por lo que es
        // —y no por cuándo se mandó— vuelve al tope y el próximo Z la reintenta, falla igual y no
        // llega nunca a las de abajo. Un fallo puntual se convierte en un Deshacer roto. Por eso se
        // reintenta, pero un número acotado de veces: a la última se descarta y la cadena sigue.
        const putBack = () => {
            const attempts = (entry.attempts || 0) + 1;
            if (attempts >= MAX_UNDO_ATTEMPTS) return UNDO_DISCARDED;   // ya salió con DROP_UNDO
            dispatch({type: 'REINSERT_UNDO', entry: {...entry, attempts}, index});
            return false;
        };

        return request.then((result) => {
            if (!undoResponseOk(result)) return putBack();
            fetchAll();
            return true;
        }).catch(putBack);
    }, [clientId, fetchAll, state.undoStack]);

    /**
     * Una persona cierra una propuesta de duplicados (`merge` / `keep_newer` / `keep_older` /
     * `keep_both`) o deshace una fusión automática (`undo`). Sin optimismo a propósito: una fusión
     * cambia DOS hechos y a veces el estado de uno (el pendiente que sobrevive se aprueba), así que
     * lo honesto es marcar los dos ocupados, mandar UNA transacción y refetchear. NO entra a la pila
     * de deshacer del mazo: el deshacer de una fusión automática es el `undo` del propio veredicto, y
     * las fusiones humanas quedan finales, como en la Memoria clásica.
     */
    const resolveDuplicate = useCallback((verdictId, action, extra = {}) => {
        const entry = [...state.duplicates.proposals, ...state.duplicates.autoMerged]
            .find(item => item && item.id === verdictId);
        const ids = pairIdsOf(entry);
        if (ids.length) dispatch({type: 'BUSY', ids, busy: true});
        return ClientMemoryApi.resolveDuplicate(verdictId, clientId, action, extra).then((data) => {
            const ok = isOkResponse(data);
            // Se ESPERA el refetch: la fila resuelta sigue dibujada hasta que la lista vuelve, y
            // soltar el ocupado antes deja sus botones activos sobre algo ya resuelto — medido en
            // ~20 s contra la base real (QA-VIS-dup-05).
            if (ok) return fetchAll().then(() => true);
            return ok;
        }).catch(() => false).finally(() => {
            if (ids.length) dispatch({type: 'BUSY', ids, busy: false});
        });
    }, [clientId, fetchAll, state.duplicates]);

    const undoAutoMerge = useCallback(
        (verdictId) => resolveDuplicate(verdictId, 'undo'),
        [resolveDuplicate],
    );

    // Memoizado: si se recreara en cada render, cualquier consumidor que lo tenga en las deps de
    // un `useMemo`/`useCallback` recomputaría siempre.
    const isBusy = useCallback((id) => state.busyIds.includes(id), [state.busyIds]);

    const value = useMemo(() => ({
        clientId,
        facts: state.facts,
        documentsById: state.documentsById,
        duplicates: state.duplicates,
        duplicatesCount: duplicatesCount(state.duplicates),
        loading: state.loading,
        loadFailed: state.loadFailed,
        busyIds: state.busyIds,
        isBusy,
        undoDepth: state.undoStack.length,
        canUndo: state.undoStack.length > 0,
        // Derivados: se calculan acá para que las tres superficies no puedan diferir en qué
        // consideran "la cola" o "la biblioteca".
        reviewQueue: reviewQueue(state.facts),
        noiseQueue: noiseQueue(state.facts),
        noiseCount: noiseCount(state.facts),
        archivedQueue: archivedQueue(state.facts),
        archivedHidden: Math.max(0, archivedTotal(state.facts) - ARCHIVED_CAP),
        archivedFailed: state.archivedFailed,
        library: libraryFacts,
        pendingCount: pending,
        actions: {
            approve,
            reject,
            removeFromArchive,
            restoreToLibrary,
            reopen,
            unarchive,
            editAndApprove,
            mergeAndApprove,
            toggleVisibility,
            removeFact,
            approveAll,
            rejectAll,
            undoLast,
            resolveDuplicate,
            undoAutoMerge,
            refetch: fetchAll,
        },
    }), [
        clientId, state, pending, libraryFacts, isBusy, approve, reject,
        removeFromArchive, restoreToLibrary, reopen, unarchive,
        editAndApprove, mergeAndApprove, toggleVisibility, removeFact, approveAll, rejectAll,
        undoLast, resolveDuplicate, undoAutoMerge, fetchAll,
    ]);

    return (
        <ClientFactsContext.Provider value={value}>
            {children}
        </ClientFactsContext.Provider>
    );
}

export function useClientFacts() {
    const ctx = useContext(ClientFactsContext);
    if (!ctx) throw new Error('useClientFacts fuera de <ClientFactsProvider>');
    return ctx;
}

export default ClientFactsProvider;
